import React, { useEffect, useMemo, useState } from 'react';
import { Link as RouterLink } from 'react-router-dom';
import { Stack, Typography, styled } from '@mui/material';
import { ADDRESS, CONTACT_MAIL, FACEBOOK, LINKEDIN, WHATSAPP_OVERDUE, WHATSAPP_REGULAR } from '../../constants';
import translation from 'i18n/translation.json';
import { ReactComponent as PhoneIcon } from 'assets/images/phone.svg';
import { ReactComponent as TimeIcon } from 'assets/images/time.svg';
import { ReactComponent as MailIcon } from 'assets/images/mail.svg';
import { ReactComponent as LinkedInIcon } from 'assets/images/whatsapp-icon-black.svg';
import { ReactComponent as FacebookIcon } from 'assets/images/facebook-icon-black.svg';
import { getCallCenterLink, getFullFormattedNumber } from 'utils';
import { RowItemFooter } from './components';
import {
  ActionLink,
  FooterColumns,
  ImgStyled,
  ItemColumn,
  ItemWrapper,
  LinkIconButton,
  RouterLinkStyled,
} from './style/general';
import { useMenuItems } from 'hooks';
import { FooterLicense } from './FooterLicense';
import { ReactComponent as WhatsAppIcon } from 'assets/images/whatsapp-icon.svg';
import { useAppSelector } from 'store/hooks';
import { selectLoan } from 'store/slices/loan';
import { selectUi } from 'store/slices/ui';
import { RouterUrl } from 'routes';
import { ManageCookiesBtn } from 'components/CookieConsent';

const socialIcons = [
  { link: FACEBOOK, icon: <FacebookIcon /> },
  { link: LINKEDIN, icon: <LinkedInIcon /> },
];

const FullFooterColumns = styled(FooterColumns)(() => ({
  paddingBottom: '40px',
}));

export const FullFooter = () => {
  const { menuItems } = useMenuItems('footer');
  const [webLinks, setWebLinks] = useState(menuItems);
  const { tel, fullPhone } = getCallCenterLink();
  const { loanInfo: loan } = useAppSelector(selectLoan);
  const { hideLinksForTestUser } = useAppSelector(selectUi);

  const whatsAppNumber = loan?.days_past_due && loan?.days_past_due >= -3 ? WHATSAPP_OVERDUE : WHATSAPP_REGULAR;

  const functionalLinks = useMemo(
    () => [
      { action: `mailto:${CONTACT_MAIL}`, icon: <MailIcon />, label: CONTACT_MAIL },
      { action: `tel:${tel}`, icon: <PhoneIcon />, label: fullPhone },
      {
        action: `https://wa.me/51${whatsAppNumber}`,
        icon: <WhatsAppIcon />,
        label: getFullFormattedNumber(whatsAppNumber),
        target: '_blank',
      },
    ],
    [loan],
  );

  useEffect(() => {
    if (hideLinksForTestUser) {
      const filteredLinks = menuItems.filter(link => link.link === RouterUrl.Reclamation);
      setWebLinks(filteredLinks);
    } else {
      setWebLinks(menuItems);
    }
  }, [menuItems, hideLinksForTestUser]);

  return (
    <>
      <FullFooterColumns>
        <ItemColumn>
          <Typography variant="h4" fontSize="18px">
            {translation.header.about}
          </Typography>
          <Stack gap="20px">
            <RouterLinkStyled to="/contacto">Contactos</RouterLinkStyled>
            {functionalLinks.map(({ action, icon, label, target }) => (
              <ActionLink href={action} className="h4" key={label} target={target ?? ''}>
                <ItemWrapper>
                  {icon}
                  <Typography variant="h4" lineHeight="30px">
                    {label}
                  </Typography>
                </ItemWrapper>
              </ActionLink>
            ))}
            <ItemWrapper>
              <TimeIcon style={{ flexShrink: 0 }} />
              <Typography variant="h4" fontWeight={400} lineHeight="30px">
                {translation.account.workSchedule}
              </Typography>
            </ItemWrapper>
          </Stack>
        </ItemColumn>
        <ItemColumn>
          <Typography variant="h4" fontSize="18px">
            Páginas útiles
          </Typography>
          <Stack gap="20px">
            {webLinks.map(({ text, link, altText, img }) => (
              <RouterLinkStyled key={text} to={link} target="_blank">
                {img ? <ImgStyled src={img} alt={text} /> : altText ?? text}
              </RouterLinkStyled>
            ))}
          </Stack>
        </ItemColumn>
        <ItemColumn>
          <Typography variant="h4" fontSize="18px">
            Síguenos en nuestras redes
          </Typography>
          <Stack flexDirection="row" gap={2.5}>
            {socialIcons.map(item => (
              <RouterLink key={item.link} to={item.link} target="_blank">
                <LinkIconButton>{item.icon}</LinkIconButton>
              </RouterLink>
            ))}
          </Stack>
        </ItemColumn>
      </FullFooterColumns>
      <RowItemFooter customstyles={{ marginBottom: { xs: '30px', md: '40px' } }} isFullFooter>
        <FooterLicense />
      </RowItemFooter>
      <Stack flexDirection="row" justifyContent="space-between" alignItems={'center'}>
        <ManageCookiesBtn /> <Typography variant="body1">Préstamos en línea - Préstamo 365 Ⓒ {ADDRESS}</Typography>
      </Stack>
    </>
  );
};
